import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef
} from 'react';

import PropTypes from 'prop-types';

/**
 * Form action types
 */
export const FORM_ACTIONS = Object.freeze({
  SET_ANSWER: 'SET_ANSWER',
  SET_ANSWERS: 'SET_ANSWERS',
  CLEAR_ANSWER: 'CLEAR_ANSWER',
  CLEAR_ANSWERS: 'CLEAR_ANSWERS',
  RESET_FORM: 'RESET_FORM',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING',
  HYDRATE_STATE: 'HYDRATE_STATE'
});

/**
 * Initial form state factory
 * @param {object} initialAnswers - Initial answers object
 * @returns {object} Initial state object
 */
export const createInitialFormState = (initialAnswers = {}) => ({
  answers: initialAnswers,
  errors: {},
  isLoading: false,
  isDirty: false,
  lastUpdated: null
});

/**
 * Form state reducer
 * @param {object} state - Current state
 * @param {object} action - Action object with type and payload
 * @returns {object} New state
 */
export const formReducer = (state, action) => {
  switch (action.type) {
    case FORM_ACTIONS.SET_ANSWER: {
      const { fieldId, value } = action.payload;
      return {
        ...state,
        answers: {
          ...state.answers,
          [fieldId]: value
        },
        isDirty: true,
        lastUpdated: new Date().toISOString(),
        errors: {
          ...state.errors,
          [fieldId]: null // Clear field error when user provides input
        }
      };
    }

    case FORM_ACTIONS.SET_ANSWERS: {
      const { answers } = action.payload;
      return {
        ...state,
        answers: {
          ...state.answers,
          ...answers
        },
        isDirty: true,
        lastUpdated: new Date().toISOString()
      };
    }

    case FORM_ACTIONS.CLEAR_ANSWER: {
      const { fieldId } = action.payload;
      const newAnswers = { ...state.answers };
      delete newAnswers[fieldId];

      return {
        ...state,
        answers: newAnswers,
        isDirty: true,
        lastUpdated: new Date().toISOString(),
        errors: {
          ...state.errors,
          [fieldId]: null
        }
      };
    }

    case FORM_ACTIONS.CLEAR_ANSWERS: {
      return {
        ...state,
        answers: {},
        isDirty: true,
        lastUpdated: new Date().toISOString(),
        errors: {}
      };
    }

    case FORM_ACTIONS.RESET_FORM: {
      const { initialAnswers = {} } = action.payload;
      return createInitialFormState(initialAnswers);
    }

    case FORM_ACTIONS.SET_ERROR: {
      const { fieldId, error } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          [fieldId]: error
        }
      };
    }

    case FORM_ACTIONS.CLEAR_ERROR: {
      const { fieldId } = action.payload;
      const newErrors = { ...state.errors };
      delete newErrors[fieldId];

      return {
        ...state,
        errors: newErrors
      };
    }

    case FORM_ACTIONS.SET_LOADING: {
      return {
        ...state,
        isLoading: action.payload.isLoading
      };
    }

    case FORM_ACTIONS.HYDRATE_STATE: {
      const { persistedState } = action.payload;
      return {
        ...state,
        ...persistedState,
        isLoading: false // Always clear loading state on hydration
      };
    }

    default:
      return state;
  }
};

/**
 * Action creators for form operations
 */
export const formActions = {
  setAnswer: (fieldId, value) => ({
    type: FORM_ACTIONS.SET_ANSWER,
    payload: { fieldId, value }
  }),

  setAnswers: (answers) => ({
    type: FORM_ACTIONS.SET_ANSWERS,
    payload: { answers }
  }),

  clearAnswer: (fieldId) => ({
    type: FORM_ACTIONS.CLEAR_ANSWER,
    payload: { fieldId }
  }),

  clearAnswers: () => ({
    type: FORM_ACTIONS.CLEAR_ANSWERS
  }),

  resetForm: (initialAnswers = {}) => ({
    type: FORM_ACTIONS.RESET_FORM,
    payload: { initialAnswers }
  }),

  setError: (fieldId, error) => ({
    type: FORM_ACTIONS.SET_ERROR,
    payload: { fieldId, error }
  }),

  clearError: (fieldId) => ({
    type: FORM_ACTIONS.CLEAR_ERROR,
    payload: { fieldId }
  }),

  setLoading: (isLoading) => ({
    type: FORM_ACTIONS.SET_LOADING,
    payload: { isLoading }
  }),

  hydrateState: (persistedState) => ({
    type: FORM_ACTIONS.HYDRATE_STATE,
    payload: { persistedState }
  })
};

/**
 * Context for managing form state across the application
 * Handles form answers, validation, persistence, and form lifecycle
 */
const CustomFormContext = createContext(null);

/**
 * Hook to use the custom form context
 * @returns {object} Context value with form state and actions
 */
const useCustomFormContext = () => {
  const context = useContext(CustomFormContext);

  if (!context) {
    throw new Error('useCustomFormContext must be used within CustomFormProvider');
  }

  return context;
};

/**
 * Custom Form Provider that manages form state and persistence
 *
 * Features:
 * - Centralized form state management
 * - Optional localStorage persistence with dynamic key registration
 * - Field-level error handling
 * - Form validation utilities
 * - Dirty state tracking
 * - Loading state management
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render within the provider
 * @param {object} [props.initialAnswers={}] - Initial form answers
 * @param {string} [props.persistKey] - Optional localStorage key for persistence (can also be set dynamically via registerPersistKey)
 * @param {boolean} [props.enablePersistence=true] - Whether to enable localStorage persistence
 * @param {Function} [props.onFormChange] - Callback when form state changes
 * @param {Function} [props.onFormSubmit] - Callback when form is submitted
 * @returns {React.ReactElement} The provider component
 */
export function CustomFormProvider({
  children,
  initialAnswers = {},
  persistKey: propPersistKey,
  enablePersistence = true,
  onFormChange,
  onFormSubmit
}) {
  const [state, dispatch] = useReducer(formReducer, createInitialFormState(initialAnswers));

  // Dynamic persistKey state - allows key to be set via prop OR registerPersistKey method
  const [activePersistKey, setActivePersistKey] = useState(propPersistKey);

  // Track if we've already hydrated for this key to prevent re-hydration on every render
  const hydratedKeyRef = useRef(null);

  // Sync with prop changes (backward compatibility)
  // If persistKey prop changes, update activePersistKey
  useEffect(() => {
    if (propPersistKey && propPersistKey !== activePersistKey) {
      setActivePersistKey(propPersistKey);
    }
  }, [propPersistKey, activePersistKey]);

  // Persist state to localStorage when it changes
  useEffect(() => {
    if (enablePersistence && activePersistKey && state.isDirty) {
      try {
        const stateToPersist = {
          answers: state.answers,
          lastUpdated: state.lastUpdated
        };
        localStorage.setItem(activePersistKey, JSON.stringify(stateToPersist));
      } catch (error) {
        console.warn('[CustomFormProvider] Failed to persist form state:', error);
      }
    }
  }, [state.answers, state.isDirty, state.lastUpdated, enablePersistence, activePersistKey]);

  // Hydrate state from localStorage when activePersistKey changes
  useEffect(() => {
    if (enablePersistence && activePersistKey && hydratedKeyRef.current !== activePersistKey) {
      try {
        const persistedState = localStorage.getItem(activePersistKey);
        if (persistedState) {
          const parsedState = JSON.parse(persistedState);
          dispatch(formActions.hydrateState(parsedState));
        }
        // Mark this key as hydrated to prevent re-hydration
        hydratedKeyRef.current = activePersistKey;
      } catch (error) {
        console.warn('[CustomFormProvider] Failed to hydrate form state:', error);
      }
    }
  }, [enablePersistence, activePersistKey]);

  // Notify parent of form changes
  useEffect(() => {
    if (onFormChange && state.isDirty) {
      onFormChange({
        answers: state.answers,
        isDirty: state.isDirty,
        lastUpdated: state.lastUpdated
      });
    }
  }, [state.answers, state.isDirty, state.lastUpdated, onFormChange]);

  // Action creators with dispatch
  const setAnswer = useCallback((fieldId, value) => {
    dispatch(formActions.setAnswer(fieldId, value));
  }, []);

  const setAnswers = useCallback((answers) => {
    dispatch(formActions.setAnswers(answers));
  }, []);

  const clearAnswer = useCallback((fieldId) => {
    dispatch(formActions.clearAnswer(fieldId));
  }, []);

  const clearAnswers = useCallback(() => {
    dispatch(formActions.clearAnswers());
  }, []);

  const resetForm = useCallback((newInitialAnswers = {}) => {
    dispatch(formActions.resetForm(newInitialAnswers));
  }, []);

  const setError = useCallback((fieldId, error) => {
    dispatch(formActions.setError(fieldId, error));
  }, []);

  const clearError = useCallback((fieldId) => {
    dispatch(formActions.clearError(fieldId));
  }, []);

  const setLoading = useCallback((isLoading) => {
    dispatch(formActions.setLoading(isLoading));
  }, []);

  /**
   * Register a persistKey dynamically (called by ChoiceFlow when it mounts)
   * This allows the provider to be placed at the app root without knowing the key upfront
   * @param {string} key - The localStorage key to use for persistence
   */
  const registerPersistKey = useCallback(
    (key) => {
      if (key && key !== activePersistKey) {
        setActivePersistKey(key);
      }
    },
    [activePersistKey]
  );

  const clearPersistence = useCallback(() => {
    if (activePersistKey) {
      try {
        localStorage.removeItem(activePersistKey);
      } catch (error) {
        console.warn('[CustomFormProvider] Failed to clear persistence:', error);
      }
    }
  }, [activePersistKey]);

  const submitForm = useCallback(
    (submitData = {}) => {
      if (onFormSubmit) {
        onFormSubmit({
          answers: state.answers,
          submitData,
          isDirty: state.isDirty,
          lastUpdated: state.lastUpdated
        });
      }
    },
    [state.answers, state.isDirty, state.lastUpdated, onFormSubmit]
  );

  // Validation utilities
  const validateField = useCallback((fieldId, value, rules = {}) => {
    const { required, minLength, maxLength, pattern, custom } = rules;

    if (required && (value === null || value === undefined || value === '')) {
      return 'This field is required';
    }

    if (value !== null && value !== undefined && value !== '') {
      if (minLength && value.length < minLength) {
        return `Must be at least ${minLength} characters`;
      }

      if (maxLength && value.length > maxLength) {
        return `Must be no more than ${maxLength} characters`;
      }

      if (pattern && !pattern.test(value)) {
        return 'Invalid format';
      }

      if (custom && typeof custom === 'function') {
        const customError = custom(value);
        if (customError) {
          return customError;
        }
      }
    }

    return null;
  }, []);

  const validateForm = useCallback(
    (validationRules = {}) => {
      const errors = {};
      let isValid = true;

      Object.entries(validationRules).forEach(([fieldId, rules]) => {
        const value = state.answers[fieldId];
        const error = validateField(fieldId, value, rules);

        if (error) {
          errors[fieldId] = error;
          isValid = false;
        }
      });

      // Set all errors at once
      Object.entries(errors).forEach(([fieldId, error]) => {
        dispatch(formActions.setError(fieldId, error));
      });

      return { isValid, errors };
    },
    [state.answers, validateField]
  );

  const contextValue = useMemo(
    () => ({
      // State
      answers: state.answers,
      errors: state.errors,
      isLoading: state.isLoading,
      isDirty: state.isDirty,
      lastUpdated: state.lastUpdated,

      // Actions
      setAnswer,
      setAnswers,
      clearAnswer,
      clearAnswers,
      resetForm,
      setError,
      clearError,
      setLoading,
      clearPersistence,
      submitForm,

      // Persistence
      registerPersistKey,
      activePersistKey,

      // Validation
      validateField,
      validateForm,

      // Utilities
      getAnswer: (fieldId) => state.answers[fieldId],
      hasAnswer: (fieldId) => state.answers[fieldId] !== undefined,
      hasErrors: Object.keys(state.errors).length > 0,
      getErrors: () => state.errors,
      getError: (fieldId) => state.errors[fieldId] || null
    }),
    [
      state.answers,
      state.errors,
      state.isLoading,
      state.isDirty,
      state.lastUpdated,
      setAnswer,
      setAnswers,
      clearAnswer,
      clearAnswers,
      resetForm,
      setError,
      clearError,
      setLoading,
      clearPersistence,
      submitForm,
      registerPersistKey,
      activePersistKey,
      validateField,
      validateForm
    ]
  );

  return <CustomFormContext.Provider value={contextValue}>{children}</CustomFormContext.Provider>;
}

CustomFormProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialAnswers: PropTypes.object,
  persistKey: PropTypes.string,
  enablePersistence: PropTypes.bool,
  onFormChange: PropTypes.func,
  onFormSubmit: PropTypes.func
};

// Export the context and hook for direct access if needed
export { CustomFormContext, useCustomFormContext };

// Export a convenience hook that uses the context
export const useCustomForm = useCustomFormContext;
